/**
 * Mermaid SVG helpers
 *
 * Renders a Mermaid_Source to a Rendered_Svg (the `.mermaid.svg` picture) with
 * the same root-element contract as an Excalidraw Diagram_File: the
 * Diagram_Title in a `<title>` element as the first child of the root `<svg>`,
 * the Diagram_Id in `data-map-diagram-id`, and `data-map-diagram-schema="1"`.
 * This lets the Lambda's existing SVG metadata reader, the thumbnail route, and
 * the embed builders treat a Mermaid Rendered_Svg exactly like an Excalidraw
 * file (see `diagramSvg.ts`, whose attribute constants are reused here).
 *
 * `renderMermaidSvg` imports `mermaid` dynamically, so importing this module
 * does not pull the heavy Mermaid bundle into the main chunk; the package loads
 * only when a render actually runs (and `mermaid.render` needs a real browser
 * DOM, not jsdom). `parseMermaid` is a thin validation wrapper around
 * `mermaid.parse`, used by the editor for inline error display. The
 * post-processing (inserting `<title>` and the `data-` attributes) is done with
 * `DOMParser` / `XMLSerializer`, matching `exportDiagramSvg`.
 */

import {
  DIAGRAM_ID_ATTRIBUTE,
  DIAGRAM_SCHEMA_ATTRIBUTE,
  DIAGRAM_SCHEMA_VERSION,
} from "@/components/diagrams/diagramSvg"

/**
 * The SVG XML namespace, used when creating the `<title>` element so it lands
 * in the SVG namespace rather than the default (HTML) one.
 */
const SVG_NAMESPACE: string = "http://www.w3.org/2000/svg"

/**
 * The MIME type used to parse the rendered SVG string back into a DOM for
 * post-processing. Typed as the `DOMParser` literal so it can be passed to
 * `parseFromString` directly.
 */
const SVG_MIME_TYPE: DOMParserSupportedType = "image/svg+xml"

/**
 * Whether `mermaid.initialize` has already run in this browser session. Mermaid
 * keeps global config, so it is initialised once and reused across renders.
 */
let mermaidInitialised: boolean = false

/**
 * A monotonically increasing counter used to give each `mermaid.render` call a
 * unique element id. Mermaid requires a unique id per render (it injects a
 * temporary element by that id); reusing one across concurrent renders would
 * clash.
 */
let renderCounter: number = 0

/**
 * Load the `mermaid` module (dynamically, to keep it out of the main bundle)
 * and initialise it once with the fixed configuration used for Diagram_Files.
 *
 * `startOnLoad` is off because the app renders on demand, never by scanning the
 * page. `securityLevel: "strict"` keeps the rendered SVG free of scripts and
 * inline event handlers, which matters because the file is committed to GitLab
 * and rendered there for signed-in members.
 *
 * @returns The initialised `mermaid` default export.
 */
async function loadMermaid(): Promise<typeof import("mermaid").default> {
  const mermaid = (await import("mermaid")).default
  if (!mermaidInitialised) {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict",
      // Diagram_Files are always rendered on a white background (Requirement
      // 2.2), so the light theme matches what GitLab shows.
      theme: "default",
    })
    mermaidInitialised = true
  }
  return mermaid
}

/**
 * Validate a Mermaid_Source without rendering it, for inline error display.
 *
 * Wraps `mermaid.parse`, which throws on a syntax error. This resolves to
 * `null` when the source is valid, or the error message when it is not, so the
 * editor can show the problem without a try/catch at every call site. It never
 * rethrows: a parse failure is an expected state while the user is typing, not
 * an exceptional one.
 *
 * @param source - The Mermaid_Source text to validate.
 * @returns `null` when the source parses, otherwise the parse error message.
 */
export async function parseMermaid(source: string): Promise<string | null> {
  try {
    const mermaid = await loadMermaid()
    await mermaid.parse(source)
    return null
  } catch (caught: unknown) {
    return caught instanceof Error ? caught.message : "Invalid Mermaid syntax."
  }
}

/**
 * Render a Mermaid_Source to a Rendered_Svg string carrying the Diagram_Id,
 * Diagram_Title, and schema version, ready to commit as the `.mermaid.svg`.
 *
 * Calls `mermaid.render` (which throws on a syntax error — the caller is
 * expected to have a valid source, e.g. a save is gated on a clean preview),
 * then post-processes the SVG the same way `exportDiagramSvg` does: insert a
 * `<title>` as the first child and set the `data-map-diagram-id` and
 * `data-map-diagram-schema` attributes on the root `<svg>`.
 *
 * `mermaid` is imported dynamically so this function only loads the package
 * when first called. It requires a real browser DOM (not jsdom).
 *
 * @param params - The render inputs.
 * @param params.source - The Mermaid_Source text to render.
 * @param params.id - The Diagram_Id to embed in the root `<svg>`.
 * @param params.title - The Diagram_Title to embed as the `<title>` element.
 * @returns The serialised Rendered_Svg text.
 * @throws Error if the Mermaid source fails to render (a syntax error).
 */
export async function renderMermaidSvg({
  source,
  id,
  title,
}: {
  source: string
  id: string
  title: string
}): Promise<string> {
  const mermaid = await loadMermaid()

  // Mermaid needs a unique element id per render; a module-level counter keeps
  // concurrent or repeated renders from clashing on the same temporary node.
  renderCounter += 1
  const renderId: string = `map-mermaid-render-${renderCounter}`

  const { svg } = await mermaid.render(renderId, source)

  // Parse the rendered SVG so the title and data- attributes can be set on the
  // real root element, rather than string-splicing. DOMParser does not throw on
  // malformed input; it returns a document whose root is not <svg> or carries a
  // <parsererror>, so reject both loudly — a Rendered_Svg we cannot parse must
  // not be committed.
  const parsed: Document = new DOMParser().parseFromString(svg, SVG_MIME_TYPE)
  const root: Element | null = parsed.documentElement
  if (root === null || root.localName !== "svg") {
    throw new Error("Mermaid did not return a valid SVG.")
  }
  if (parsed.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Mermaid returned a malformed SVG.")
  }

  // Insert the <title> as the first child (in the SVG namespace), replacing any
  // title Mermaid may already have emitted so the Diagram_Title is authoritative.
  const existingTitle: Element | null = root.getElementsByTagName("title").item(0)
  if (existingTitle !== null && existingTitle.parentNode === root) {
    root.removeChild(existingTitle)
  }
  const titleElement: Element = parsed.createElementNS(SVG_NAMESPACE, "title")
  titleElement.textContent = title
  root.insertBefore(titleElement, root.firstChild)

  root.setAttribute(DIAGRAM_ID_ATTRIBUTE, id)
  root.setAttribute(DIAGRAM_SCHEMA_ATTRIBUTE, DIAGRAM_SCHEMA_VERSION)

  return new XMLSerializer().serializeToString(root)
}
